import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './PopupCard.css';

const TIME_OPTIONS = [10, 15, 30];

function PopupCard({
  imageSrc,
  category = 'GOURMET BURGERS',
  title = 'The Truffle Burger Project',
  customerName = 'Alex Rivers',
  address = 'No location',
  orderId = 'ORD-7742',
  orderTime = '12:45 PM',
  isNew = true,
  showTimePicker = false,   // true only for company role
  readyInMins,              // shows an ETA badge instead of picker (carrier view)
  onRemove,
  onAccept,                 // called with (selectedMinutes) when showTimePicker, else no args
}) {
  const { t } = useTranslation();
  const [selectedTime, setSelectedTime] = useState(15);

  const handleAccept = () => {
    if (showTimePicker) {
      onAccept && onAccept(selectedTime);
    } else {
      onAccept && onAccept();
    }
  };

  const displayAddress = () => {
    if (typeof address === 'object' && address !== null) {
      const parts = [];
      if (address.street) parts.push(address.street);
      if (address.city) parts.push(address.city);
      return parts.length > 0 ? parts.join(', ') : t('popupCard.noLocation');
    }
    return typeof address === 'string' && address.trim() !== '' ? address : t('popupCard.noLocation');
  };

  return (
    <div className="popup-card">
      {/* ── Image Section ──────────────────────────────── */}
      <div className="popup-card__image-wrap">
        {imageSrc ? (
          <img src={imageSrc} alt={title} className="popup-card__image" />
        ) : (
          <div className="popup-card__image-placeholder" />
        )}
        {isNew && (
          <span className="popup-card__badge">
            <span className="popup-card__badge-dot" />
            {t('popupCard.newOrder')}
          </span>
        )}
      </div>

      {/* ── Body ────────────────────────────────────────── */}
      <div className="popup-card__body">
        <p className="popup-card__category">{category}</p>

        {/* Title */}
        <h2 className="popup-card__title">{title}</h2>

        {/* Address row */}
        <div className="popup-card__address-row">
          <svg
            className="popup-card__pin-icon"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z"
              fill="currentColor"
            />
          </svg>
          <div className="popup-card__address-text">
            <span className="popup-card__customer-name">{customerName}</span>
            <span className="popup-card__address">{displayAddress()}</span>
          </div>
        </div>

        {/* Divider */}
        <div className="popup-card__divider" />

        {/* Order meta */}
        <div className="popup-card__meta-row">
          <span className="popup-card__order-id">{t('popupCard.orderId')}{orderId}</span>
          <span className="popup-card__order-time">{orderTime}</span>
        </div>

        {/* ── Time picker (company only) ─────────────── */}
        {showTimePicker && (
          <div className="popup-card__time-picker">
            <span className="popup-card__time-label">
              <svg className="popup-card__clock-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
                <path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {t('popupCard.readyIn')}
            </span>
            <div className="popup-card__time-options">
              {TIME_OPTIONS.map(mins => (
                <button
                  key={mins}
                  className={`popup-card__time-opt ${selectedTime === mins ? 'popup-card__time-opt--active' : ''}`}
                  onClick={() => setSelectedTime(mins)}
                  type="button"
                >
                  {mins}m
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── ETA badge (carrier view) ───────────────── */}
        {!showTimePicker && readyInMins != null && (
          <div className="popup-card__eta-row">
            <svg className="popup-card__clock-icon popup-card__clock-icon--eta" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
              <path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="popup-card__eta-text">
              {t('popupCard.pickUpIn')} <strong>{readyInMins} {t('popupCard.min')}</strong>
            </span>
          </div>
        )}

        {/* Actions */}
        <div className="popup-card__actions">
          <button
            type="button"
            className="popup-card__btn popup-card__btn--remove"
            onClick={onRemove}
          >
            {t('popupCard.remove')}
          </button>
          <button
            type="button"
            className="popup-card__btn popup-card__btn--accept"
            onClick={handleAccept}
          >
            <svg
              className="popup-card__accept-icon"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
              <path
                d="M7.5 12.5l3 3 6-6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {showTimePicker ? t('popupCard.acceptOrder') : t('popupCard.takeDelivery')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PopupCard;